/* Core & libraries */
import React, { useState, useEffect } from 'react';

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  TRANSFER_MILES_ENDPOINT,
  GET_PROGRAM_DETAILS_ENDPOINT,
  GET_HOME_PAGE_STATIC_EN_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_ES_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_FR_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_PT_CONTENT_ENDPOINT,
  GET_HOME_PAGE_STATIC_PT_BR_CONTENT_ENDPOINT,
} from "../endpoints/endpoints";
import { useUserContext } from "../contexts/userContext";
import { useGlobalContext, useGlobalUpdateContext } from "../contexts/globalContext";
import { useLocaleContext } from "../contexts/localeContext";

/* Components */
/* Styles */

export const useHomepageService = () => {
  const globalData = useGlobalContext();
  const setGlobalData = useGlobalUpdateContext();
  const userDetails = useUserContext();
  const locale = useLocaleContext();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const [programDetails, setProgramDetails] = useState(globalData.programDetails || {});
  const [staticContent, setStaticContent] = useState(globalData.staticContent || {})
  const { genericApiFetch } = useGenericApiFetch();

  useEffect(() => {
    if (programDetails && staticContent) {
      setGlobalData({
        ...globalData,
        programDetails: programDetails,
        staticContent: staticContent,
        companyEntityId: companyEntityId
      });
    }
  }, [programDetails, staticContent])

  const transferMiles = async (transferMilesData) => {
    const response = await genericApiFetch({
      url: TRANSFER_MILES_ENDPOINT.replace(
        "{companyEntityId}",
        companyEntityId
      ).replace("{providerCode}", providerCode?.toLowerCase()),
      method: "POST",
      data: transferMilesData,
      spinner: "transferMilesSpinner",
    });

    if (response.data?.errors) {
      return response;
    }
    return false;
  };

  const proxyContentCalls = async () => {
    getProgramDetails()
    getStaticContent()
  }

  const getProgramDetails = async (refreshProgramDetails) => {
    if (!refreshProgramDetails && globalData.programDetails && companyEntityId === globalData.companyEntityId) {
      return true;
    }

    const response = await genericApiFetch({
      url: GET_PROGRAM_DETAILS_ENDPOINT.replace(
        "{companyEntityId}",
        companyEntityId
      ).replace("{providerCode}", providerCode?.toLowerCase()),
      method: "GET",
    });
    if (response.success) {
      setProgramDetails(response.data);
      return true;
    }
    return false;
  };

  const getStaticContent = async () => {
    const localeEndpointMapping = (() => {
      const endpointGroups = {
        [GET_HOME_PAGE_STATIC_EN_CONTENT_ENDPOINT]: [
          'en_AU', 'en_US', 'de_DE', 'en_DE', 'en_IE', 'en_NZ', 
          'en_CA', 'en_ES', 'en_XL', 'en_IN', 'en_UK', 'it_IT'
        ],
        [GET_HOME_PAGE_STATIC_ES_CONTENT_ENDPOINT]: [
          'es_ES', 'es_US', 'en_MX', 'es_XL', 'es_MX', 
          'es_CO', 'es_CL', 'es_PE', 'es_AR'
        ],
        [GET_HOME_PAGE_STATIC_FR_CONTENT_ENDPOINT]: [
          'en_FR', 'fr_FR', 'fr_CA'
        ],
        [GET_HOME_PAGE_STATIC_PT_CONTENT_ENDPOINT]: [
          'pt_PT'
        ],
        [GET_HOME_PAGE_STATIC_PT_BR_CONTENT_ENDPOINT]: [
          'pt_BR', 'pt_XL'
        ]
      };

      return Object.entries(endpointGroups).reduce((mapping, [endpoint, locales]) => {
        locales.forEach(locale => mapping[locale] = endpoint);
        return mapping;
      }, {});
    })();

    let currentEndpointText = localeEndpointMapping[locale];

    if(currentEndpointText) {
      const response = await genericApiFetch({
        url: currentEndpointText.replace("{providerCode}", providerCode?.toLowerCase()),
        method: "GET",
      });
      if (response.success) {
        setStaticContent(response.data);
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    getStaticContent();
  }, [locale]);

  return {
    programDetails,
    staticContent,
    setProgramDetails,
    transferMiles,
    getProgramDetails,
    getStaticContent,
    proxyContentCalls
  };
};
